import React, { useState } from 'react';
import { useNotifications } from '../contexts/NotificationContext';

const MetaCampaignTrackingQuickActionModal = ({ record, onClose, onSave, currentUser }) => {
  const [actionType, setActionType] = useState('status_change');
  const [newStatus, setNewStatus] = useState(record?.status || 'pendiente');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotifications();

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente', color: 'text-yellow-600' },
    { value: 'en_proceso', label: '🔄 En Proceso', color: 'text-blue-600' },
    { value: 'completado', label: '✅ Completado', color: 'text-green-600' },
    { value: 'cancelado', label: '❌ Cancelado', color: 'text-red-600' }
  ];

  const actionTypes = [
    { value: 'status_change', label: '🔄 Cambiar Estado', description: 'Actualizar el estado del seguimiento' },
    { value: 'add_comment', label: '💬 Agregar Comentario', description: 'Añadir observaciones o notas' },
    { value: 'mark_completed', label: '✅ Marcar Completado', description: 'Finalizar el seguimiento' },
    { value: 'mark_cancelled', label: '❌ Marcar Cancelado', description: 'Cancelar el seguimiento' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!comment.trim() && actionType === 'add_comment') {
      showNotification('Por favor ingresa un comentario', 'error');
      return;
    }

    setLoading(true);
    
    try {
      let actionData = {
        action_type: actionType,
        user_name: currentUser?.firstName || currentUser?.name || currentUser?.email || 'Usuario',
        details: comment.trim(),
        created_at: new Date().toISOString()
      };

      // Configurar datos específicos según el tipo de acción
      switch (actionType) {
        case 'status_change':
          actionData.action_type = 'status_changed';
          actionData.field_changed = 'status';
          actionData.previous_value = record.status;
          actionData.new_value = newStatus;
          actionData.new_status = newStatus;
          break;
          
        case 'add_comment':
          actionData.action_type = 'comment_added';
          break;
          
        case 'mark_completed':
          actionData.action_type = 'completed';
          actionData.field_changed = 'status';
          actionData.previous_value = record.status;
          actionData.new_value = 'completado';
          actionData.new_status = 'completado';
          break;
          
        case 'mark_cancelled':
          actionData.action_type = 'cancelled';
          actionData.field_changed = 'status';
          actionData.previous_value = record.status;
          actionData.new_value = 'cancelado';
          actionData.new_status = 'cancelado';
          break;
      }

      await onSave(record.id, actionData);
      showNotification('Acción registrada exitosamente', 'success');
      onClose();
      
    } catch (error) {
      console.error('Error al guardar acción:', error);
      showNotification('Error al registrar la acción: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStatusLabel = () => {
    const status = statusOptions.find(s => s.value === record?.status);
    return status ? status.label : 'Estado desconocido';
  };

  const getNewStatusLabel = () => {
    const status = statusOptions.find(s => s.value === newStatus);
    return status ? status.label : 'Estado desconocido';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <span className="mr-3">⚡</span>
                Acción Rápida
              </h2>
              <p className="text-orange-100 mt-1">
                Seguimiento de Campaña: {record?.campana || 'N/A'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Información del registro */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4 mb-6 border border-orange-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Cliente:</p>
                <p className="text-lg font-semibold text-gray-900">{record?.nombre_cliente}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Estado Actual:</p>
                <p className="text-lg font-semibold text-orange-600">{getCurrentStatusLabel()}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Campaña:</p>
                <p className="text-lg font-semibold text-gray-900">{record?.campana}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Célula:</p>
                <p className="text-lg font-semibold text-gray-900">{record?.celula}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tipo de acción */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Selecciona una acción:
              </label>
              <div className="grid grid-cols-1 gap-3">
                {actionTypes.map(action => (
                  <label
                    key={action.value}
                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                      actionType === action.value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="actionType"
                      value={action.value}
                      checked={actionType === action.value}
                      onChange={(e) => setActionType(e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="text-xl mr-3">{action.label.split(' ')[0]}</span>
                        <div>
                          <p className="font-medium text-gray-900">{action.label.substring(2)}</p>
                          <p className="text-sm text-gray-500">{action.description}</p>
                        </div>
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      actionType === action.value
                        ? 'border-orange-500 bg-orange-500'
                        : 'border-gray-300'
                    }`}>
                      {actionType === action.value && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Selector de estado (solo para cambio de estado) */}
            {actionType === 'status_change' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nuevo Estado:
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                
                {newStatus !== record?.status && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Cambio:</span> {getCurrentStatusLabel()} → {getNewStatusLabel()}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Campo de comentario */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {actionType === 'add_comment' ? 'Comentario *' : 'Comentario (opcional)'}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  actionType === 'add_comment' 
                    ? 'Escribe tu comentario aquí...'
                    : 'Agrega detalles adicionales sobre esta acción...'
                }
                rows="4"
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 resize-none"
                required={actionType === 'add_comment'}
              />
              <p className="text-xs text-gray-500 mt-1">
                {actionType === 'add_comment' 
                  ? 'El comentario es obligatorio para esta acción'
                  : 'Opcional: Proporciona contexto adicional sobre la acción'
                }
              </p>
            </div>

            {/* Preview de la acción */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Vista previa de la acción:</h4>
              <div className="text-sm text-gray-600">
                <p><span className="font-medium">Usuario:</span> {currentUser?.firstName || currentUser?.name || currentUser?.email || 'Usuario'}</p>
                <p><span className="font-medium">Acción:</span> {actionTypes.find(a => a.value === actionType)?.label}</p>
                {actionType === 'status_change' && newStatus !== record?.status && (
                  <p><span className="font-medium">Cambio de estado:</span> {getCurrentStatusLabel()} → {getNewStatusLabel()}</p>
                )}
                {(actionType === 'mark_completed' || actionType === 'mark_cancelled') && (
                  <p><span className="font-medium">Nuevo estado:</span> {actionType === 'mark_completed' ? '✅ Completado' : '❌ Cancelado'}</p>
                )}
                {comment.trim() && (
                  <p><span className="font-medium">Comentario:</span> {comment.trim()}</p>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || (actionType === 'add_comment' && !comment.trim())}
            className="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Procesando...
              </div>
            ) : (
              '⚡ Ejecutar Acción'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaCampaignTrackingQuickActionModal;
